import { Banner, Button, Label, Typography, toast } from '@neo4j-ndl/react';
import { GithubIcon } from '@neo4j-ndl/react/icons';
import type { DbmsInfo, DbmsPlugin } from '@nx/state/desktop';
import {
  getRelateErrors,
  useInstallPluginMutation,
  useRestartDbms,
  useUninstallPluginMutation,
} from '@nx/state/desktop';
import { isNotNullish } from '@nx/stdlib';
import { useEffect } from 'react';

import { useToggleInstanceRunStatus } from '../hooks/use-toggle-instance-run-status';
import { PLUGINS_INFO } from './plugins-info';

export function Plugin({
  instance,
  plugin,
  pluginId,
  loading,
}: {
  instance: DbmsInfo;
  plugin: DbmsPlugin & { isInstalled: boolean };
  pluginId: string;
  loading: boolean;
}) {
  const [installPlugin, install] = useInstallPluginMutation();
  const [uninstallPlugin, uninstall] = useUninstallPluginMutation();
  const restartDbms = useRestartDbms();
  const { isStopped, isStopping, isStarting } = useToggleInstanceRunStatus(instance);

  const pluginInfo = PLUGINS_INFO[pluginId];

  const onInstall = () => {
    void installPlugin({ dbmsId: instance.id, pluginName: pluginId });
  };

  const onUninstall = () => {
    void uninstallPlugin({ dbmsId: instance.id, pluginName: pluginId });
  };

  useEffect(() => {
    const result = install.isError ? install : uninstall;
    if (result.isError) {
      const errors = getRelateErrors(result.error);
      const toastMessage = (
        <span>
          <Typography variant="subheading-medium">{`Something went wrong while ${result === uninstall ? 'un' : ''}installing the plugin "${pluginInfo?.name}".`}</Typography>
          <br />
          {errors}
        </span>
      );
      toast.danger(toastMessage, { isCloseable: true });
    }
  }, [install, uninstall, pluginInfo]);

  const installUninstallButton = plugin.isInstalled ? (
    <Button size="small" color="danger" onClick={onUninstall} isLoading={uninstall.isLoading}>
      Uninstall
    </Button>
  ) : (
    <Button size="small" onClick={onInstall} isLoading={install.isLoading}>
      Install
    </Button>
  );

  return (
    <div className="my-5">
      <div className="flex items-baseline justify-between">
        <Typography variant="subheading-medium">{pluginInfo?.name ?? pluginId}</Typography>

        <div className="flex items-baseline gap-2">
          {plugin.isInstalled && (
            <Label color="success" fill="outlined">
              Installed
            </Label>
          )}
          <Typography variant="body-medium" className="text-palette-neutral-text-weaker">
            {plugin.version}
          </Typography>
        </div>
      </div>

      {pluginInfo && (
        <Typography variant="body-medium" as="div" className="pt-3">
          {pluginInfo.description}
        </Typography>
      )}

      <div className="mt-4 flex gap-2">
        {loading ? (
          <Button
            isLoading
            size="small"
            fill="outlined"
            color="neutral"
            htmlAttributes={{ 'aria-label': 'Loading plugin actions' }}
          >
            {''}
          </Button>
        ) : (
          installUninstallButton
        )}

        {isNotNullish(pluginInfo) && (
          <Button
            as="a"
            size="small"
            color="neutral"
            fill="outlined"
            href={pluginInfo.links.documentation}
            target="_blank"
          >
            Documentation
          </Button>
        )}

        {isNotNullish(pluginInfo?.links.github) && (
          <Button as="a" size="small" color="neutral" fill="outlined" href={pluginInfo.links.github} target="_blank">
            <GithubIcon className="mr-1" /> Github
          </Button>
        )}
      </div>

      {plugin.pendingRestart && !isStopped && !isStarting && !isStopping && (
        <Banner
          className="mt-4"
          description={'Restart Instance to finish plugin installation'}
          hasIcon
          isCloseable={false}
          type="info"
          usage="inline"
          actions={[
            {
              label: 'Restart',
              onClick: () => {
                void restartDbms(instance.id);
              },
            },
          ]}
        />
      )}
    </div>
  );
}
